import { ColumnDefByColName } from '../../../../../common/$models/ModelDef'
import { makeItReactive } from '../../../../../front/$frameworkUtils/$frameworkUtils'
import { IDLike } from '../../../../../types'
import { registerComposableComponentSettings } from '../../../../ComposableComponents'
import { extractVisibleOnIndexFromColumns } from '../../../../ModelServices/modelFactoryUtils'
import { ComposableDataListService } from '../../ComposableDataListService'
import { dataListComposableColumnNameSelectionSettingColumnDef } from '../../patrials/dataListComposableColumnNameSelectionSettingColumnDef'

export type DataListDisplayColumn = {
  label: string
  name: string
  visible?: boolean // 明示的に false なら 非表示
  order?: number // 表示順 (あるかもしれないし、無いかもしれない)
}

export type DataListDisplayUserConfiguredColumn = {
  colName: DataListDisplayColumn['name']
  visible: DataListDisplayColumn['visible']
  order: DataListDisplayColumn['order']
}

export type DataListDisplayUserConfig = {
  modelName: string
  virtualModelName?: string
  columns: DataListDisplayUserConfiguredColumn[]
} & (
  | {
      applyToModelNameBased: true // モデル名に基づいて適用するかどうか
      urlPath?: string
    }
  | {
      applyToModelNameBased: false // モデル名に基づいて適用するかどうか
      urlPath: string // 設定されていれば この画面だけ... という意味になる
    }
)

/**
 * # DataListDisplayService
 *
 * ## 役割
 * - DataListDisplayコンポーネント用, 表示するカラムやその他機能設定を保持する
 * - ComposableDataListService に依存
 *
 *
 */
export class DataListDisplayService {
  public static readonly componentName = 'DataListDisplayContainer'
  // 列に利用するカラムを選択するかどうか
  public isSpecifyColumns = false
  // 表示するカラム
  public specifiedColumns: DataListDisplayColumn[] = []
  // 表示するカラム (ユーザが設定したもの)
  public userConfiguredColumns: DataListDisplayUserConfiguredColumn[] = []
  // 一括操作を有効にするか
  public disableBulkActions = false
  // TODO: 一括操作の選択肢
  public bulkActions: any[] = []
  private _checkedItemId: { [key: string]: boolean } = makeItReactive({})
  public bulkChecked = false
  public itemClickFunction: (itemId: IDLike, event, record) => Promise<void> = null
  public lastChecked: string = null
  public renderKey = 0

  constructor(
    public readonly ComposableDataListServiceInstance: ComposableDataListService,
  ) {}

  reRender() {
    this.renderKey = new Date().getTime()
  }

  get modelNameBasedDisplayKey(): string {
    return `DataListDisplayService--${this.ComposableDataListServiceInstance.modelName}--${this.ComposableDataListServiceInstance.virtualModelName || ''}`
  }

  private get columnsVisibleConfigByModelNameWithCurrentUser(): {
    [key: string]: DataListDisplayUserConfig['columns'][number]
  } {
    const userMetaDataKey = `${this.modelNameBasedDisplayKey}--columnsVisibleConfig`
    console.log('userMetaDataKey', userMetaDataKey)
    // TODO: 現在の ModelName, VirtualModelName, に基づいて ログイン中のユーザ の metaData として 設定を取得する
    const config = $core.$userMetaData.get(userMetaDataKey)?.columns || []
    return config.reduce((acc, cur) => {
      acc[cur.colName] = cur
      return acc
    }, {})
  }

  get columnsWithoutUserConfig(): ColumnDefByColName {
    if (this.isSpecifyColumns !== true || !this.specifiedColumns?.length) {
      const visibleColumns = this.visibleColumns
      const colKeys = Object.keys(visibleColumns)
      return colKeys
        .sort((a, b) => {
          // orderOnList (asc) による sort
          const aOrder =
            this.ComposableDataListServiceInstance.columns[a]?.orderOnList || 9999
          const bOrder =
            this.ComposableDataListServiceInstance.columns[b]?.orderOnList || 9999
          return aOrder - bOrder >= 0 ? 1 : -1
        })
        .reduce((acc, cur) => {
          acc[cur] = visibleColumns[cur]
          return acc
        }, {})
    }
    return (
      this.specifiedColumns
        // .sort((a, b) => (a.order || 999) - (b.order || 999))
        .reduce((acc, cur) => {
          if (!cur?.name || cur?.visible === false) {
            return acc
          }
          const colDef = this.ComposableDataListServiceInstance.columns[cur.name]
          if (!colDef || colDef.visible === false) {
            return acc
          }
          acc[cur.name] = { ...colDef }
          if (cur.label) {
            acc[cur.name].label = cur.label
          }
          return acc
        }, {})
    )
  }

  /**
   * 利用するColumnsを返す, 基本的には Table表示 `DataListDisplayTable` で利用される
   */
  get columns(): ColumnDefByColName {
    const columnsWithoutUserConfig = this.columnsWithoutUserConfig
    if (this.isSpecifyColumns !== true || !this.specifiedColumns?.length) {
      const colKeys = Object.keys(columnsWithoutUserConfig)
      const columnsVisibleConfigByModelNameWithCurrentUser =
        this.columnsVisibleConfigByModelNameWithCurrentUser
      return colKeys
        .sort((a, b) => {
          // userConfig による sort
          const aOrder = columnsVisibleConfigByModelNameWithCurrentUser[a]?.order || 9999
          const bOrder = columnsVisibleConfigByModelNameWithCurrentUser[b]?.order || 9999
          return aOrder - bOrder >= 0 ? 1 : -1
        })
        .reduce((acc, cur) => {
          // userConfig による visible の設定
          const visible = columnsVisibleConfigByModelNameWithCurrentUser[cur]?.visible
          if (visible === false) {
            return acc
          }
          acc[cur] = columnsWithoutUserConfig[cur]
          return acc
        }, {})
    }
    return columnsWithoutUserConfig
  }

  get visibleColumns(): ColumnDefByColName {
    return extractVisibleOnIndexFromColumns(
      this.ComposableDataListServiceInstance.columns,
    )
  }

  get checkedItemId(): { [key: string]: boolean } {
    return this._checkedItemId
  }
  checkItemId(itemId: IDLike, checked: boolean) {
    if (checked) {
      this._checkedItemId[itemId] = !!checked
    } else {
      delete this._checkedItemId[itemId]
    }
  }
  bulkCheckItemIds(checked: boolean) {
    this.bulkChecked = !!checked
    if (!checked) {
      this._checkedItemId = {}
      return
    }
    this._checkedItemId = this.ComposableDataListServiceInstance.items.reduce(
      (acc, cur) => {
        acc[cur.id] = !!checked
        return acc
      },
      {},
    )
  }

  /**
   * Item click 時の処理を実行する
   * @param itemId
   * @param event
   * @param record
   */
  async invokeItemClickFunction(itemId: IDLike, event, record) {
    if (this.itemClickFunction) {
      this.itemClickFunction(itemId, event, record)
      return
    }
    const modelDefinedItemClick =
      this.ComposableDataListServiceInstance.getModelProp('indexListItemOnClick')
    if (modelDefinedItemClick) {
      await modelDefinedItemClick(itemId, event, record)
      return
    }
    // default
    const modalArgs = {
      id: itemId,
      modelName: this.ComposableDataListServiceInstance.modelName,
      virtualModelName: this.ComposableDataListServiceInstance.virtualModelName,
    }
    if (this.ComposableDataListServiceInstance.updatable) {
      $core.$modals.openEditViewModal(modalArgs)
    } else {
      $core.$modals.openModelViewModal(modalArgs)
    }
  }

  get shouldBulkControlEnabled(): boolean {
    if (
      this.ComposableDataListServiceInstance.getPermission('bulkControllable') === false
    ) {
      return false
    }
    return this.bulkControlOptions?.length > 0 && this.disableBulkActions !== true
  }

  get bulkControlOptions(): { value: { action: string }; text: string }[] {
    const options: { value: { action: string }; text: string }[] = []
    if (this.ComposableDataListServiceInstance.updatable !== false) {
      options.push({ value: { action: 'bulkEdit' }, text: '一括編集' })
    }
    if (this.ComposableDataListServiceInstance.deletable !== false) {
      options.push({ value: { action: 'bulkDelete' }, text: '一括削除' })
    }
    if ($core.$embAuth.user.isAdmin) {
      options.push({ value: { action: 'exportAsJson' }, text: 'JSONエクスポート' })
    }
    const bulkControlActions =
      this.ComposableDataListServiceInstance.virtualModel?.bulkControlActions ||
      this.ComposableDataListServiceInstance.model.bulkControlActions
    if (bulkControlActions && Object.keys(bulkControlActions)?.length) {
      Object.keys(bulkControlActions).map((key) => {
        options.push({
          value: { action: key },
          text: bulkControlActions[key].label || key,
        })
      })
    }
    return options
  }
}

const configColumnsOfDataListDisplayContainer: ColumnDefByColName = {
  isSpecifyColumns: {
    label: '表示するカラムを指定する',
    type: 'BOOLEAN',
    inputHelpText:
      'チェックを入れない場合は、モデル定義のカラムが表示対象になります。 ※ 表示形式がTableの場合のみ有効',
    defaultValue: false,
  },
  specifiedColumns: {
    label: '表示するカラム',
    type: 'ARRAY_OF_OBJECT',
    enableIf: (row) => !!row.isSpecifyColumns,
    columns: {
      name: {
        label: 'カラム',
        ...dataListComposableColumnNameSelectionSettingColumnDef,
        type: 'STRING',
      },
      label: {
        label: '表示名',
        type: 'STRING',
      },
    },
  },
  disableBulkActions: {
    label: '一括操作を無効にする',
    type: 'BOOLEAN',
    defaultValue: false,
  },
}

// Builder 用の定義
registerComposableComponentSettings(DataListDisplayService.componentName, {
  hasDefaultSlot: true,
  label: DataListDisplayService.componentName,
  configColumns: configColumnsOfDataListDisplayContainer,
  category: 'DataListDisplay',
})
